<template>
  <div class="invite-register-page">
    <button
      v-if="showLanguageMenu"
      type="button"
      class="register-language-backdrop"
      :aria-label="t('common.close')"
      @click="showLanguageMenu = false"
    />

    <div class="register-language-wrap">
      <button
        type="button"
        class="register-language"
        :aria-expanded="showLanguageMenu"
        @click="showLanguageMenu = !showLanguageMenu"
      >
        <AppSvgIcon class="register-language__icon" name="sphere" />
        <span>{{ currentLanguageLabel }}</span>
        <AppSvgIcon
          class="register-language__arrow"
          name="language-arrow"
          :class="{ 'register-language__arrow--open': showLanguageMenu }"
        />
      </button>

      <section v-if="showLanguageMenu" class="register-language__menu">
        <div
          v-for="item in languageOptions"
          :key="item.key"
          class="register-language__option"
          :class="{
            'register-language__option--active': item.key === currentLocale,
          }"
          @click="selectLanguage(item.key)"
        >
          <span>{{ item.displayLabel }}</span>
          <small
            v-if="item.subLabel"
            :class="{
              'register-language__option-subtitle--empty': !item.subLabel,
            }"
          >
            {{ item.subLabel || "\u00a0" }}
          </small>
        </div>
      </section>
    </div>

    <div
      class="register-hero"
      :class="{
        'register-hero--two-lines': registerTitleLineCount === 2,
        'register-hero--three-lines': registerTitleLineCount >= 3,
      }"
    >
      <div ref="registerTitleRef" class="register-title">
        <div class="register-title-left">
          {{ t("auth.inviteRegisterTitleStart") }}
        </div>
        <div class="register-title-right">
          {{ t("auth.inviteRegisterTitleEnd") }}
        </div>
      </div>
      <div class="register-icon">
        <div class="icon">
          <app-image :src="ChromeIcon" alt="" />
          <div>Pay</div>
        </div>
        <div class="icon">
          <AppSvgIcon name="invite-apple" size="24" color="#0b184d" />
          <div>Pay</div>
        </div>
      </div>
    </div>
    <div class="register-hero-img">
      <app-image :src="themeStore.image('img1', img1)" alt="" />
    </div>
    <div class="register-bottom">
      <van-button
        class="register-button"
        :icon="ArrowRightIcon"
        icon-position="right"
        @click="openRegisterPopup"
      >
        {{ t("auth.inviteRegisterStartRewards") }}
        <template #icon>
          <AppSvgIcon name="invite-arrow-right" size="24" color="#fff" />
        </template>
      </van-button>
    </div>

    <van-popup
      v-model:show="showRegisterPopup"
      position="bottom"
      round
      class="register-popup"
      overlay-class="register-overlay"
      :close-on-click-overlay="true"
    >
      <div class="register-dialog__title">
        {{ t("auth.inviteRegisterWelcome") }}
      </div>
      <section class="register-dialog">
        <div class="register-dialog__code">
          <i />
          <span>{{ t("auth.invitationCode") }}:</span>
          <div data-strong>{{ invitationCode || "--" }}</div>
          <i />
        </div>

        <van-field
          v-model="registerEmail"
          class="register-dialog__email"
          type="email"
          clearable
          :placeholder="t('auth.enterEmailAddress')"
        >
          <template #left-icon>
            <van-icon name="envelop-o" />
          </template>
        </van-field>

        <van-button
          block
          round
          class="register-dialog__submit"
          :disabled="!registerEmailValid"
          @click="startRegister"
        >
          {{ t("auth.register") }}
        </van-button>

        <div
          class="register-dialog__agreement"
          :class="{
            'register-dialog__agreement--shaking': registerAgreementShaking,
          }"
        >
          <div class="read" @click="registerAgreed = !registerAgreed">
            <AppSvgIcon
              v-if="registerAgreed"
              name="icon"
              size="16"
              color="var(--wp-color-theme)"
            />
            <AppSvgIcon v-else name="icon" size="16" color="#C9CDD4" />
          </div>
          <p>
            {{ t("auth.agreePrefix") }}
            <em role="button" tabindex="0" @click.stop="openPrivacyPolicy"
              >《{{ t("auth.privacyPolicy") }}》</em
            >
            {{ t("auth.and") }}
            <em role="button" tabindex="0" @click.stop="openUserAgreement"
              >《{{ t("auth.userAgreement") }}》</em
            >
          </p>
        </div>
      </section>
    </van-popup>
    <!--  v-model:show="showVerificationPopup" -->
    <van-popup
      v-model:show="showVerificationPopup"
      position="bottom"
      round
      class="register-code-popup"
      overlay-class="register-code-overlay"
      :close-on-click-overlay="false"
    >
      <section class="register-code-dialog">
        <header class="register-code-dialog__head">
          <h2>{{ t("auth.enterVerificationCode") }}</h2>
          <button
            type="button"
            :aria-label="t('common.close')"
            @click="closeVerificationPopup"
          >
            <app-image
              class="app-cancel-icon register-code-dialog__close-icon"
              src="@/assets/images/cancel_gray.png"
              alt=""
            />
          </button>
        </header>

        <p class="register-code-dialog__tip">
          {{ t("auth.codeSentToEmail") }}
        </p>
        <p class="register-code-dialog__email-tip">
          <span>{{ registerEmail }}</span>
          <em>{{ t("auth.codeValid") }}</em>
        </p>

        <div
          class="register-code-inputs"
          role="group"
          :aria-label="t('auth.emailCode')"
        >
          <input
            v-for="(_, index) in verificationDigits"
            :key="index"
            :ref="(element) => setCodeInputRef(element, index)"
            v-model="verificationDigits[index]"
            class="register-code-input"
            inputmode="numeric"
            maxlength="1"
            autocomplete="one-time-code"
            @input="handleCodeInput(index, $event)"
            @keydown="handleCodeKeydown(index, $event)"
            @paste="handleCodePaste(index, $event)"
          />
        </div>

        <div class="register-code-dialog__actions">
          <button
            type="button"
            :disabled="sendingVerification || verificationCountdown > 0"
            @click="resendVerificationCode"
          >
            {{
              verificationCountdown > 0
                ? `${t("auth.resend")}${verificationCountdown}s`
                : t("auth.resend")
            }}
          </button>
          <button type="button" @click="handleCodeHelp">
            {{ t("auth.cannotReceiveCode") }}
          </button>
        </div>

        <van-button
          block
          round
          class="register-code-dialog__submit"
          :disabled="verificationCode.length !== verificationDigits.length"
          :loading="verifyingRegistration"
          @click="verifyRegistrationCode"
        >
          {{ t("auth.verify") }}
        </van-button>
      </section>
    </van-popup>

    <van-popup
      v-model:show="showCodeHelpPopup"
      position="bottom"
      round
      class="register-code-help-popup"
      overlay-class="register-code-help-overlay"
      :close-on-click-overlay="false"
    >
      <section class="register-code-help-dialog">
        <header class="register-code-help-dialog__head">
          <h2>{{ t("auth.codeHelpTitle") }}</h2>
          <button
            type="button"
            :aria-label="t('common.close')"
            @click="closeCodeHelp"
          >
            <app-image
              class="app-cancel-icon register-code-help-dialog__close-icon"
              src="@/assets/images/cancel_gray.png"
              alt=""
            />
          </button>
        </header>

        <p class="register-code-help-dialog__intro">
          {{ t("auth.codeHelpIntro") }}
        </p>

        <div class="register-code-help-dialog__item">
          <h3>{{ t("auth.codeHelpEmailTitle") }}</h3>
          <p>{{ t("auth.codeHelpEmailDesc") }}</p>
        </div>
        <div class="register-code-help-dialog__item">
          <h3>{{ t("auth.codeHelpSpamTitle") }}</h3>
          <p>{{ t("auth.codeHelpSpamDesc") }}</p>
        </div>
        <div class="register-code-help-dialog__item">
          <h3>{{ t("auth.codeHelpResendTitle") }}</h3>
          <p>{{ t("auth.codeHelpResendDesc") }}</p>
        </div>
      </section>
    </van-popup>

    <van-popup
      v-model:show="showPasswordPopup"
      position="bottom"
      round
      class="register-password-popup"
      overlay-class="register-password-overlay"
      :close-on-click-overlay="false"
    >
      <section class="register-password-dialog">
        <header class="register-password-dialog__head">
          <h2>{{ t("auth.inputPassword") }}</h2>
          <button
            type="button"
            :aria-label="t('common.close')"
            @click="closePasswordPopup"
          >
            <app-image
              class="app-cancel-icon register-password-dialog__close-icon"
              src="@/assets/images/cancel_gray.png"
              alt=""
            />
          </button>
        </header>

        <div class="register-password-dialog__body">
          <label class="register-password-dialog__label">
            <span>{{ t("auth.strongPassword") }}</span>
            <em>*</em>
          </label>
          <AppMaskedPasswordField
            v-model="walletPassword"
            class="register-password-field"
            :placeholder="t('auth.enterPassword')"
            :visible="showWalletPassword"
          >
            <template #right-icon>
              <button
                type="button"
                class="register-password-field__toggle"
                :aria-label="t('common.close')"
                @click="showWalletPassword = !showWalletPassword"
              >
                <app-image
                  :src="showWalletPassword ? eyeOpenIcon : eyeCloseIcon"
                  alt=""
                />
              </button>
            </template>
          </AppMaskedPasswordField>
          <div
            v-if="walletPassword"
            class="register-password-strength"
            :class="`register-password-strength--${walletPasswordStrength}`"
          >
            <div
              v-show="walletPassword.length >= 8"
              class="register-password-strength__bars"
            >
              <i
                v-for="item in 3"
                :key="item"
                :class="{ active: item <= walletPasswordStrength }"
              />
            </div>
            <span>{{ walletPasswordStrengthText }}</span>
          </div>

          <label
            class="register-password-dialog__label register-password-dialog__label--confirm"
          >
            <span>{{ t("auth.confirmPassword") }}</span>
            <em>*</em>
          </label>
          <AppMaskedPasswordField
            v-model="walletPasswordAgain"
            class="register-password-field"
            :placeholder="t('auth.enterPassword')"
            :visible="showWalletPasswordAgain"
            :class="{
              'register-password-field--error':
                walletPasswordAgain && walletPasswordAgain !== walletPassword,
            }"
          >
            <template #right-icon>
              <button
                type="button"
                class="register-password-field__toggle"
                :aria-label="t('common.close')"
                @click="showWalletPasswordAgain = !showWalletPasswordAgain"
              >
                <app-image
                  :src="showWalletPasswordAgain ? eyeOpenIcon : eyeCloseIcon"
                  alt=""
                />
              </button>
            </template>
          </AppMaskedPasswordField>
          <div
            v-if="walletPasswordAgain"
            class="register-password-strength"
            :class="`register-password-strength--${walletPasswordAgainStrength}`"
          >
            <div
              v-show="walletPasswordAgain.length >= 8"
              class="register-password-strength__bars"
            >
              <i
                v-for="item in 3"
                :key="item"
                :class="{ active: item <= walletPasswordAgainStrength }"
              />
            </div>
            <span>{{ walletPasswordAgainStrengthText }}</span>
          </div>

          <van-button
            block
            round
            type="primary"
            class="register-password-dialog__submit"
            :disabled="passwordDisabled"
            :loading="creatingWallet"
            @click="createWallet"
          >
            {{ t("auth.createWallet") }}
          </van-button>
        </div>
      </section>
    </van-popup>
  </div>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { showFailToast } from "vant";
import { showPassedToast } from "@/utils/toast";
import { useRouter } from "vue-router";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  emailCheck,
  emailRegister,
  emailSend,
  userCheckInvitationCode,
} from "@/api/auth";
import { useProfileStore } from "@/stores/profile";
import { useWebModalStore } from "@/stores/web-modal";
import i18n from "@/i18n";
import {
  APP_LANGUAGE_OPTIONS,
  applyAppLocale,
  resolveLocale,
} from "@/utils/locale";
import { getDeviceId } from "@/utils/device";
import {
  saveWalletMnemonic,
  saveWalletPassword,
  saveWalletSignerSecret,
} from "@/utils/walletSession";
import { deriveAccountAddresses } from "@/utils/walletAddress";
import { clearRouteState, navigateWithState } from "@/utils/route-state";
import { useThemeStore } from "@/stores/theme";
import AppMaskedPasswordField from "@/components/AppMaskedPasswordField.vue";
import ChromeIcon from "@/assets/images/Invite/chrome.png";
import eyeOpenIcon from "@/assets/images/eye_open_icon.png";
import eyeCloseIcon from "@/assets/images/eye_close_icon.png";
import img1 from "@/assets/images/img1.png";

const router = useRouter();
const route = useRoute();
const { t, locale } = useI18n();
const profileStore = useProfileStore();
const themeStore = useThemeStore();
const webModalStore = useWebModalStore();
const showRegisterPopup = ref(false);
const showVerificationPopup = ref(false);
const showCodeHelpPopup = ref(false);
const showPasswordPopup = ref(false);
const showLanguageMenu = ref(false);
const registerEmail = ref("");
const registerAgreed = ref(false);
const registerAgreementShaking = ref(false);
const verificationDigits = ref(Array.from({ length: 6 }, () => ""));
const codeInputRefs = ref([]);
const sendingVerification = ref(false);
const verifyingRegistration = ref(false);
const creatingWallet = ref(false);
const verificationCountdown = ref(0);
const verifiedRegistrationCode = ref("");
const walletPassword = ref("");
const walletPasswordAgain = ref("");
const showWalletPassword = ref(false);
const showWalletPasswordAgain = ref(false);
const registerTitleRef = ref(null);
const registerTitleLineCount = ref(1);
let verificationTimer = null;

const invitationCode = computed(() =>
  String(route.query.r || route.query.invitationCode || "").trim(),
);
const registerEmailValid = computed(() =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registerEmail.value.trim()),
);
const verificationCode = computed(() => verificationDigits.value.join(""));
const passwordDisabled = computed(
  () => !walletPassword.value || !walletPasswordAgain.value,
);
const isZhLocale = computed(() =>
  String(locale.value || "")
    .toLowerCase()
    .startsWith("zh"),
);
const privacyPolicyUrl = computed(
  () =>
    themeStore.url?.[
      `privacy_policy_${isZhLocale.value ? "zh-CN" : "en-US"}`
    ] ||
    (isZhLocale.value
      ? "https://winpay.com/privacy_policy_zh-CN.html"
      : "https://winpay.com/privacy_policy_en-US.html"),
);
const userAgreementUrl = computed(
  () =>
    themeStore.url?.[
      `user_agreement_${isZhLocale.value ? "zh-CN" : "en-US"}`
    ] ||
    (isZhLocale.value
      ? "https://api.winpay.dev/user_agreement_zh-CN.html"
      : "https://winpay.com/user_agreement_en-US.html"),
);

const currentLocale = computed(() =>
  resolveLocale(locale.value || profileStore.locale || "zh-CN"),
);
const languageOptions = computed(() =>
  APP_LANGUAGE_OPTIONS.map((item) => {
    const display = {
      "zh-CN": { displayLabel: "简体中文" },
      "zh-HK": { displayLabel: "繁體中文" },
      "en-US": { displayLabel: "English" },
      "vi-VN": { displayLabel: "VI", subLabel: "Tiếng Việt" },
      "id-ID": { displayLabel: "ID", subLabel: "Bahasa Indonesia" },
      "ms-MY": { displayLabel: "MS", subLabel: "Bahasa Melayu" },
    }[item.key] || { displayLabel: item.label };
    return { ...item, ...display };
  }),
);
const currentLanguageLabel = computed(() => {
  const current = currentLocale.value;
  return (
    languageOptions.value.find((item) => item.key === current)?.displayLabel ||
    "简体中文"
  );
});

function selectLanguage(key) {
  const nextLocale = applyAppLocale(i18n, key);
  profileStore.setLocale(nextLocale);
  showLanguageMenu.value = false;
}

function updateRegisterTitleLineCount() {
  const title = registerTitleRef.value;
  if (!title) return;

  const lineHeight = Number.parseFloat(
    window.getComputedStyle(title).lineHeight,
  );
  if (!lineHeight) return;

  registerTitleLineCount.value = Math.max(
    1,
    Math.round(title.scrollHeight / lineHeight),
  );
}

function getPasswordStrength(value) {
  if (value.length < 8) return 0;
  let count = 0;
  if (/[A-Z]/.test(value)) count += 1;
  if (/[a-z]/.test(value)) count += 1;
  if (/\d/.test(value)) count += 1;
  if (/[!@#$&*~^%()\-_+=.,;:]/.test(value)) count += 1;
  if (value.length >= 12 && count === 4) return 3;
  if (value.length >= 12 && count >= 3) return 2;
  return 1;
}

const walletPasswordStrength = computed(() =>
  getPasswordStrength(walletPassword.value),
);
const walletPasswordAgainStrength = computed(() =>
  getPasswordStrength(walletPasswordAgain.value),
);
const walletPasswordStrengthText = computed(() =>
  walletPasswordStrength.value === 3
    ? t("auth.passwordStrong")
    : walletPasswordStrength.value === 2
      ? t("auth.passwordMedium")
      : walletPasswordStrength.value === 1
        ? t("auth.passwordWeak")
        : t("auth.passwordTooShort"),
);
const walletPasswordAgainStrengthText = computed(() =>
  walletPasswordAgainStrength.value === 3
    ? t("auth.passwordStrong")
    : walletPasswordAgainStrength.value === 2
      ? t("auth.passwordMedium")
      : walletPasswordAgainStrength.value === 1
        ? t("auth.passwordWeak")
        : t("auth.passwordTooShort"),
);

function openRegisterPopup() {
  showRegisterPopup.value = true;
}

function triggerRegisterAgreementWarning() {
  registerAgreementShaking.value = false;
  requestAnimationFrame(() => {
    registerAgreementShaking.value = true;
  });
}

function startVerificationCountdown() {
  verificationCountdown.value = 60;
  window.clearInterval(verificationTimer);
  verificationTimer = window.setInterval(() => {
    verificationCountdown.value -= 1;
    if (verificationCountdown.value <= 0) {
      window.clearInterval(verificationTimer);
      verificationTimer = null;
    }
  }, 1000);
}

async function sendVerificationCode() {
  if (sendingVerification.value || verificationCountdown.value > 0) {
    return false;
  }

  sendingVerification.value = true;
  try {
    const result = await emailSend({
      token: getDeviceId(),
      email: registerEmail.value.trim(),
      type: "REGISTER",
      label: "ZORPAY",
    });
    if (!result && result !== true) {
      return false;
    }
    startVerificationCountdown();
    return true;
  } catch (error) {
    showFailToast(error?.msg || error?.message || t("common.retryLater"));
    return false;
  } finally {
    sendingVerification.value = false;
  }
}

async function startRegister() {
  if (!registerAgreed.value) {
    triggerRegisterAgreementWarning();
    return;
  }
  if (!registerEmailValid.value) {
    return;
  }

  const sent = await sendVerificationCode();
  if (!sent) {
    return;
  }
  showRegisterPopup.value = false;
  showVerificationPopup.value = true;
  verificationDigits.value = Array.from({ length: 6 }, () => "");
  await nextTick();
  codeInputRefs.value[0]?.focus();
}

function setCodeInputRef(element, index) {
  if (element) {
    codeInputRefs.value[index] = element;
  }
}

function focusCodeInput(index) {
  codeInputRefs.value[
    Math.max(0, Math.min(index, verificationDigits.value.length - 1))
  ]?.focus();
}

function handleCodeInput(index, event) {
  const value = String(event.target.value || "").replace(/\D/g, "");
  verificationDigits.value[index] = value.slice(-1);
  if (value) {
    focusCodeInput(index + 1);
  }
}

function handleCodeKeydown(index, event) {
  if (
    event.key === "Backspace" &&
    !verificationDigits.value[index] &&
    index > 0
  ) {
    verificationDigits.value[index - 1] = "";
    focusCodeInput(index - 1);
  }
}

function handleCodePaste(index, event) {
  event.preventDefault();
  const pasted = String(event.clipboardData?.getData("text") || "")
    .replace(/\D/g, "")
    .slice(0, verificationDigits.value.length - index);
  pasted.split("").forEach((digit, offset) => {
    verificationDigits.value[index + offset] = digit;
  });
  focusCodeInput(index + pasted.length);
}

async function resendVerificationCode() {
  await sendVerificationCode();
}

function closeVerificationPopup() {
  showVerificationPopup.value = false;
  verificationDigits.value = Array.from({ length: 6 }, () => "");
}

function closePasswordPopup() {
  showPasswordPopup.value = false;
  walletPassword.value = "";
  walletPasswordAgain.value = "";
  showWalletPassword.value = false;
  showWalletPasswordAgain.value = false;
}

function handleCodeHelp() {
  showVerificationPopup.value = false;
  showCodeHelpPopup.value = true;
}

async function closeCodeHelp() {
  showCodeHelpPopup.value = false;
  showVerificationPopup.value = true;
  await nextTick();
  focusCodeInput(verificationDigits.value.findIndex((digit) => !digit));
}

async function verifyRegistrationCode() {
  if (verificationCode.value.length !== verificationDigits.value.length) {
    showFailToast(t("auth.codeRequired"));
    return;
  }

  verifyingRegistration.value = true;
  const code = verificationCode.value.trim();
  try {
    const result = await emailCheck({
      token: getDeviceId(),
      email: registerEmail.value.trim(),
      type: "REGISTER",
      code,
    });
    if (!result && result !== true) {
      return;
    }

    verifiedRegistrationCode.value = verificationCode.value;
    showVerificationPopup.value = false;
    showPasswordPopup.value = true;
  } catch (error) {
    showFailToast(error?.msg || error?.message || t("auth.verificationFailed"));
  } finally {
    verifyingRegistration.value = false;
  }
}

function generateRandomName() {
  const letters = "abcdefghijklmnopqrstuvwxyz";
  const length = Math.floor(Math.random() * 6) + 3;
  let result = String.fromCharCode(65 + Math.floor(Math.random() * 26));
  for (let index = 1; index < length; index += 1) {
    result += letters[Math.floor(Math.random() * letters.length)];
  }
  return result;
}

async function buildWallet() {
  const { Wallet } = await import("ethers");
  const wallet = Wallet.createRandom();
  return {
    address: wallet.address,
    mnemonic: wallet.mnemonic?.phrase || "",
  };
}

async function createWallet() {
  if (walletPassword.value.length < 8 || walletPasswordAgain.value.length < 8) {
    showFailToast(t("auth.passwordTooShort"));
    return;
  }
  if (walletPassword.value !== walletPasswordAgain.value) {
    showFailToast(t("auth.passwordMismatch"));
    return;
  }

  creatingWallet.value = true;
  try {
    if (invitationCode.value) {
      const invitationResult = await userCheckInvitationCode({
        invitationCode: invitationCode.value,
      });
      if (
        invitationResult?.isCorrect !== 1 &&
        invitationResult?.isCorrect !== true
      ) {
        throw new Error(invitationResult?.msg || t("auth.verificationFailed"));
      }
    }

    const wallet = await buildWallet();
    const nickName = generateRandomName();
    const registerResult = await emailRegister({
      email: registerEmail.value.trim(),
      token: getDeviceId(),
      code: verifiedRegistrationCode.value,
      address: wallet.address,
      payPwd: walletPassword.value,
      nickName,
      invitationCode: invitationCode.value,
      label: "ZORPAY",
    });
    if (!registerResult) {
      return;
    }

    saveWalletMnemonic(wallet.address, wallet.mnemonic);
    saveWalletSignerSecret(wallet.address, wallet.mnemonic, "mnemonic");
    const derivedAccount = await deriveAccountAddresses(
      wallet.mnemonic,
      "mnemonic",
    );
    await profileStore.setCurrentAccount({
      ...derivedAccount,
      address: wallet.address,
      name: nickName,
      email: registerEmail.value.trim(),
      walletWay: "ByEmail",
      type: "0",
      isBackup: false,
    });
    profileStore.setWalletType("createEmail");
    saveWalletPassword(wallet.address, walletPassword.value);
    profileStore.setFirstVisit(true);
    if (registerResult?.token) {
      profileStore.setToken(registerResult.token);
    }
    showPasswordPopup.value = false;
    showPassedToast(t("auth.walletReady"));
    if (invitationCode.value) {
      navigateWithState(router, "/home/index", {
        namespace: "home-index",
        replace: true,
        payload: {
          openCardCodePopupAfterExplore: true,
        },
      });
    } else {
      clearRouteState("home-index");
      router.replace("/home/index");
    }
  } catch (error) {
    showFailToast(error?.msg || error?.message || t("common.retryLater"));
  } finally {
    creatingWallet.value = false;
  }
}

function openPrivacyPolicy() {
  webModalStore.open({
    url: privacyPolicyUrl.value,
    title: t("auth.privacyPolicy"),
    navBackgroundColor: "#fff",
  });
}

function openUserAgreement() {
  webModalStore.open({
    url: userAgreementUrl.value,
    title: t("auth.userAgreement"),
    navBackgroundColor: "#fff",
  });
}

onMounted(() => {
  nextTick(updateRegisterTitleLineCount);
  window.addEventListener("resize", updateRegisterTitleLineCount);
});

watch(locale, () => nextTick(updateRegisterTitleLineCount));

onBeforeUnmount(() => {
  window.clearInterval(verificationTimer);
  window.removeEventListener("resize", updateRegisterTitleLineCount);
});
</script>

<style scoped lang="scss">
.van-button--disabled {
  background: #f0f0f0 !important;
  color: #999 !important;
}
.invite-register-page {
  position: relative;
  width: 100%;
  height: var(--app-viewport-height);
  min-height: var(--app-viewport-height);
  overflow: hidden;
  // background: url("@/assets/images/Invite/InviteBg.png") center center / cover
  //   no-repeat;
  background: var(--wp-color-card-3);

  :global(.register-popup.van-popup--bottom) {
    width: var(--app-shell-width);
    max-width: 100vw;
    left: var(--app-shell-left);
    right: auto;
    overflow: visible;
    background: transparent;
  }

  :global(.register-overlay) {
    width: var(--app-shell-width);
    max-width: 100vw;
    left: var(--app-shell-left);
    right: auto;
  }

  :global(.register-code-popup.van-popup--bottom) {
    width: var(--app-shell-width);
    max-width: 100vw;
    left: var(--app-shell-left);
    right: auto;
    overflow: hidden;
    background: #ffffff;
  }

  :global(.register-code-overlay) {
    width: var(--app-shell-width);
    max-width: 100vw;
    left: var(--app-shell-left);
    right: auto;
  }

  :global(.register-code-help-popup.van-popup--bottom) {
    width: var(--app-shell-width);
    max-width: 100vw;
    max-height: calc(100vh - 112px);
    left: var(--app-shell-left);
    right: auto;
    overflow-y: auto;
    background: #fff;
  }

  :global(.register-code-help-overlay) {
    width: var(--app-shell-width);
    max-width: 100vw;
    left: var(--app-shell-left);
    right: auto;
  }

  :global(.register-password-popup.van-popup--bottom) {
    width: var(--app-shell-width);
    max-width: 100vw;
    left: var(--app-shell-left);
    right: auto;
    overflow: visible;
    background: transparent;
  }

  :global(.register-password-overlay) {
    width: var(--app-shell-width);
    max-width: 100vw;
    left: var(--app-shell-left);
    right: auto;
  }

  // Touch tablets keep the same mobile composition as the app shell. Without
  // this limit the background is scaled to the full iPad width and gets cropped.
  @media (min-width: 768px) and (pointer: coarse) {
    width: min(100%, 430px);
    margin: 0 auto;

    :global(.register-popup.van-popup--bottom),
    :global(.register-overlay),
    :global(.register-code-popup.van-popup--bottom),
    :global(.register-code-overlay),
    :global(.register-code-help-popup.van-popup--bottom),
    :global(.register-code-help-overlay),
    :global(.register-password-popup.van-popup--bottom),
    :global(.register-password-overlay) {
      width: min(100vw, 430px);
      left: 50%;
      right: auto;
      transform: translateX(-50%);
    }
  }

  .register-language-backdrop {
    position: fixed;
    inset: 0;
    z-index: 10;
    padding: 0;
    border: 0;
    background: transparent;
  }

  .register-language-wrap {
    position: absolute;
    top: 24px;
    right: 12px;
    z-index: 11;
  }

  .register-language {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 10px;
    border: 0;
    border-radius: 30px;
    background: #ffffff;
    color: #000;
    font-size: 14px;
    text-align: left;
    font-weight: 500;
    appearance: none;
    border: 1px solid #d0d8ec;

    &__icon {
      width: 18px;
      height: 18px;
      flex-shrink: 0;
    }

    &__arrow {
      width: 10px;
      height: 7px;
      flex-shrink: 0;
      transition: transform 0.2s ease;

      &--open {
        transform: rotate(180deg);
      }
    }

    &__menu {
      position: absolute;
      top: calc(100% + 12px);
      right: 0;
      display: grid;
      width: 124px;
      padding: 10px 0;
      border: 1px solid #cbd8ef;
      border-radius: 12px;
      background: #ffffff;
      box-shadow: 0 2px 8px rgba(44, 76, 142, 0.16);
      box-sizing: border-box;
    }

    &__option {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      height: 66px;
      padding: 15px 15px 10px;
      border: 0;
      background: transparent;
      color: #222222;
      font-size: 16px;
      line-height: 22px;
      text-align: left;
      font-weight: 500;
      box-sizing: border-box;

      > span,
      small {
        display: block;
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      small {
        min-height: 18px;
        margin-top: 1px;
        color: #999999;
        font-size: 10px;
        font-weight: 400;
        line-height: 18px;
      }

      &-subtitle--empty {
        margin-top: 0;
      }

      &--active {
        color: #000000;
        background: #f2f6fd;
      }
    }
  }

  .register-hero {
    position: absolute;
    top: 20%;
    left: 16px;
    width: calc(100% - 32px);
    display: flex;
    flex-direction: column;
    align-items: center;

    &--two-lines {
      top: 17%;
    }

    &--three-lines {
      top: 14%;
    }
  }
  .register-hero-img {
    position: absolute;
    top: 34%;
    display: flex;
    justify-content: center;
    width: 100%;

    :deep(.app-image),
    :deep(.van-image__img) {
      display: block;
      width: 100%;
      height: auto;
      object-fit: contain;
    }
  }

  .register-title {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: baseline;
    white-space: normal;
    font-size: 35px;
    line-height: 1.25;
    text-align: center;

    &-left {
      max-width: 100%;
      color: #0b184d;
      font-weight: 700;
      overflow-wrap: anywhere;
      .app-image {
        width: 18px;
        height: 18px;
      }
    }
    &-right {
      max-width: 100%;
      color: var(--wp-color-theme);
      font-weight: 700;
      overflow-wrap: anywhere;
      .app-image {
        width: 18px;
        height: 18px;
      }
    }
  }
  .register-icon {
    margin-top: 24px;
    width: 100%;
    display: flex;
    justify-content: center;
    white-space: nowrap;
    font-size: 16px;
    gap: 0 20px;
    font-weight: 500;
    .icon {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 3px 15px;
      border-radius: 50px;
      border: 1px solid #0b184d;
      .app-image,
      .app-svg-icon {
        width: 24px;
        height: 24px;
        margin-right: 5px;
      }
      &:last-child .app-image,
      &:last-child .app-svg-icon {
        margin-right: 5px;
        margin-left: 5px;
      }
    }
  }
  .register-bottom {
    position: absolute;
    bottom: 8%;
    left: 0;
    width: 100%;
    padding: 0 20px;
    box-sizing: border-box;
    display: flex;
    font-size: 20px;
    justify-content: center;
  }

  @media (max-width: 390px) and (max-height: 700px) {
    .register-hero-img {
      top: 29%;
    }

    .register-hero {
      top: 15%;

      &--two-lines {
        top: 12%;
      }

      &--three-lines {
        top: 10%;
      }
    }
  }

  .register-button {
    position: relative;
    display: block;
    width: 70%;
    height: 48px;
    font-size: 18px;
    font-weight: 500;
    border-radius: 50px;
    background: linear-gradient(
      90deg,
      var(--wp-color-brand-1) 0%,
      var(--wp-color-brand-3) 100%
    );
    box-shadow: 0 3px 5.4px 0 rgba(255, 255, 255, 0.6) inset;
    border-top: 0px solid var(--wp-color-brand-3);
    border-left: 0px solid var(--wp-color-brand-3);
    border-right: 0px solid var(--wp-color-brand-3);
    border-bottom: 3px solid var(--wp-color-brand-2);
    color: #fff;

    :deep(.van-button__content) {
      position: relative;
    }

    :deep(.van-button__text) {
      margin: 0 auto;
      max-width: 80%;
    }

    :deep(.van-button__icon) {
      position: absolute;
      top: 50%;
      right: 0px;
      margin: 0;
      transform: translateY(-50%);
      img {
        width: 24px;
        height: 24px;
      }
    }
  }

  .register-dialog {
    position: relative;
    padding: 42px 20px 60px;
    box-sizing: border-box;
    background: #ffffff;
    border-radius: 22px 22px 0 0;
    z-index: 99;

    &__title {
      position: absolute;
      top: -42px;
      left: 0;
      padding: 10px 30px 40px 26px;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      border-radius: 20px 20px 0 0;
      background: linear-gradient(
        90deg,
        var(--wp-color-brand-1) 0%,
        var(--wp-color-brand-3) 100%
      );
      color: #ffffff;
      font-size: 18px;
      font-weight: 600;
      z-index: -1;
    }

    &__code {
      display: flex;
      align-items: center;
      gap: 16px;
      color: #333333;
      font-size: 18px;
      line-height: 24px;
      white-space: nowrap;

      i {
        flex: 1;
        height: 1px;
        background: #ced9ee;
      }
      [data-strong] {
        font-weight: 700;
      }
    }

    &__email {
      margin-top: 20px;
      padding: 10px 14px;
      border: 1px solid #cbd8ef;
      border-radius: 15px;
      background: #f7f7f8;
      box-sizing: border-box;

      :deep(.van-field__left-icon) {
        margin-right: 10px;
        color: var(--wp-color-theme);
        font-size: 22px;
      }

      :deep(.van-field__control) {
        color: #333333;
        font-size: 16px;
      }
      :deep(.van-icon-clear) {
        font-size: 18px;
      }
    }

    &__submit {
      margin-top: 26px;
      border: 0;
      background: var(--wp-color-brand-1);
      color: #ffffff;
      font-size: 18px;
      font-weight: 500;
    }

    &__agreement {
      display: flex;
      align-items: flex-start;
      gap: 5px;
      margin-top: 16px;

      &--shaking {
        animation: register-agreement-shake 360ms ease-in-out;
      }

      .read {
        flex: 0 0 auto;
        width: 16px;
        height: 16px;
        margin-top: 2px;
        padding: 0;
        border: 0;
        background: transparent;

        .app-image {
          display: block;
          width: 16px;
          height: 16px;
        }

        .app-svg-icon {
          display: block;
          vertical-align: initial;
        }
      }

      p {
        flex: 1;
        margin: 0;
        color: #333333;
        font-size: 14px;
        line-height: 20px;
      }

      em {
        color: var(--wp-color-theme);
        font-style: normal;
      }
    }
  }

  .register-code-dialog {
    padding: 30px 20px 30px;
    box-sizing: border-box;
    background: #ffffff;

    &__head {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 32px;

      h2 {
        margin: 0;
        color: #111111;
        font-size: 22px;
        font-weight: 600;
        line-height: 32px;
      }

      button {
        position: absolute;
        top: -2px;
        right: -2px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        border: 0;
        background: transparent;
        color: #666666;

        .register-code-dialog__close-icon {
          display: block;
          width: 22px;
          height: 22px;
        }
      }
    }

    &__tip {
      margin: 20px 0 0;
      color: #666666;
      font-size: 17px;
      font-weight: 400;
      line-height: 24px;
    }

    &__email-tip {
      display: flex;
      align-items: baseline;
      margin: 0;
      color: var(--wp-color-brand-1);
      font-size: 17px;
      font-weight: 500;
      line-height: 26px;
      white-space: nowrap;

      span {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      em {
        flex: 0 0 auto;
        color: #666666;
        font-style: normal;
        font-size: 15px;
      }
    }
  }

  .register-code-inputs {
    display: flex;
    gap: 10px;
    margin-top: 22px;
  }

  .register-code-input {
    width: 0;
    min-width: 0;
    flex: 1;
    height: 42px;
    padding: 0;
    border: 1px solid #d5e0f2;
    border-radius: 8px;
    outline: none;
    background: #ffffff;
    color: #000;
    font-size: 22px;
    font-weight: 500;
    line-height: 42px;
    text-align: center;

    &:focus {
      border-color: var(--wp-color-brand-1);
    }
  }

  .register-code-dialog__actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 17px;
    font-weight: 500;

    button {
      padding: 0;
      border: 0;
      background: transparent;
      font-size: 16px;
      line-height: 24px;

      &:first-child,
      &:first-child:disabled {
        color: var(--wp-color-brand-1);
      }

      &:last-child {
        color: #ff1c3d;
      }
    }
  }

  .register-code-dialog__submit {
    margin-top: 30px;
    border: 0;
    background: #f0f0f0;
    color: #9f9f9f;
    font-size: 18px;
    font-weight: 500;

    &:not(:disabled) {
      background: var(--wp-color-theme);
      color: #ffffff;
    }
  }

  .register-code-help-dialog {
    padding: 30px 20px 32px;
    box-sizing: border-box;
    background: #ffffff;

    &__head {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 32px;

      h2 {
        margin: 0;
        color: #111111;
        font-size: 20px;
        font-weight: 700;
      }

      button {
        position: absolute;
        top: 0;
        right: -2px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        border: 0;
        background: transparent;

        .register-code-help-dialog__close-icon {
          display: block;
          width: 22px;
          height: 22px;
        }
      }
    }

    &__intro {
      margin: 24px 0 28px;
      color: var(--wp-color-theme);
      font-size: 18px;
      font-weight: 500;
      line-height: 28px;
    }

    &__item {
      position: relative;
      padding-left: 18px;

      &::before {
        position: absolute;
        top: 7px;
        left: 0;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #17213f;
        content: "";
      }

      h3 {
        margin: 0;
        color: #17213f;
        font-size: 18px;
        font-weight: 600;
        line-height: 26px;
      }

      p {
        margin: 10px 0 28px;
        color: #333333;
        font-size: 16px;
        line-height: 26px;
      }

      &:last-child p {
        margin-bottom: 0;
      }
    }
  }

  .register-password-dialog {
    overflow: hidden;
    border-bottom: 0;
    border-radius: 24px 24px 0 0;
    background: #ffffff;

    &__head {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 58px;
      padding: 0 44px;

      h2 {
        margin: 0;
        color: #111111;
        font-size: 20px;
        font-weight: 600;
        line-height: 28px;
      }

      button {
        position: absolute;
        top: 50%;
        right: 12px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        padding: 0;
        border: 0;
        background: transparent;
        transform: translateY(-50%);
      }
    }

    &__close-icon {
      display: block;
      width: 22px;
      height: 22px;
    }

    &__body {
      padding: 0 16px 28px;
    }

    &__label {
      display: flex;
      align-items: baseline;
      gap: 4px;
      margin: 14px 0 8px;
      color: #333333;
      font-size: 16px;
      line-height: 22px;

      em {
        color: #ff3b30;
        font-style: normal;
      }

      &--confirm {
        margin-top: 26px;
      }
    }

    &__submit {
      height: 48px;
      margin-top: 30px;
      border: 0;
      background: var(--wp-color-brand-1);
      font-size: 16px;
      font-weight: 500;
    }
  }

  .register-password-field {
    padding: 10px 12px;
    border-radius: 10px;
    background: #f4f5f6;

    :deep(.van-field__control) {
      color: #333333;
      font-size: 16px;
    }

    :deep(.van-field__control::placeholder) {
      color: #8a8a8a;
    }

    &__toggle {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      border: 0;
      background: transparent;

      .app-image {
        display: block;
        width: 18px;
        height: 18px;
      }
    }
  }

  .register-password-strength {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 18px;
    margin: 5px 0 0 16px;

    &__bars {
      display: flex;
      flex: 1;
      gap: 10px;

      i {
        display: block;
        width: 38px;
        height: 4px;
        border-radius: 4px;
        background: #d9d9d9;

        &.active {
          background: #ff7d00;
        }
      }
    }

    span {
      flex: 0 0 auto;
      color: #ff7d00;
      font-size: 13px;
      line-height: 18px;
    }

    &--0 {
      justify-content: flex-start;
      margin-left: 0;

      > span {
        color: red;
        text-align: left;
      }
    }

    &--2,
    &--3 {
      .register-password-strength__bars i.active {
        background: #07b077;
      }

      > span {
        color: #07b077;
      }
    }
  }
}

@keyframes register-agreement-shake {
  0%,
  100% {
    transform: translateX(0);
  }

  20% {
    transform: translateX(-4px);
  }

  40% {
    transform: translateX(4px);
  }

  60% {
    transform: translateX(-3px);
  }

  80% {
    transform: translateX(3px);
  }
}
</style>
